import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Award, Home as HomeIcon, MapPin, Truck } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { BUSINESS, SERVICES, SERVICE_ORDER, WHY, CAPABILITIES, WORK_PREVIEW, SERVICE_AREAS, GALLERY, IMG } from "@/data/site";

const WHY_ICONS = { award: Award, home: HomeIcon, map: MapPin, truck: Truck };

const heroLine = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.7, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

function Hero() {
  return (
    <section className="relative flex items-center overflow-hidden border-b border-line">
      <img
        src={IMG("20240215_133946.jpg")}
        alt="Park & Spark mobile welding on a truck flatbed"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-ink/75" aria-hidden="true" />
      <div
        className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-ink/30"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40 w-full">
        <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-gold flex items-center gap-3">
          <span className="inline-block h-px w-8 bg-gold" aria-hidden="true" />
          {BUSINESS.tagline}
        </p>
        <h1 className="font-display font-bold uppercase tracking-tight text-slate-100 text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mt-5">
          <span className="block overflow-hidden">
            <motion.span
              className="block"
              variants={heroLine}
              custom={0}
              initial="hidden"
              animate="show"
            >
              Park &amp; Spark
            </motion.span>
          </span>
          <span className="block overflow-hidden text-gold">
            <motion.span
              className="block"
              variants={heroLine}
              custom={1}
              initial="hidden"
              animate="show"
            >
              Mobile Welding
            </motion.span>
          </span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 max-w-xl text-slate-300 text-base sm:text-lg leading-relaxed"
        >
          Mobile welding and metal fabrication for commercial, agricultural, and residential
          clients — we bring our expertise and equipment to your location.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.62, ease: [0.22, 1, 0.36, 1] }}
          className="mt-9 flex flex-col sm:flex-row gap-3"
        >
          <Link
            to="/contact"
            data-testid="hero-estimate-button"
            className="inline-flex items-center justify-center gap-2 bg-gold px-7 py-4 text-sm font-bold uppercase tracking-wider text-ink hover:bg-gold-light transition-colors"
          >
            Request a Free Estimate
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
          <a
            href={BUSINESS.phoneHref}
            data-testid="hero-call-button"
            className="inline-flex items-center justify-center gap-2 border-2 border-slate-500 px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-white hover:border-gold hover:text-gold transition-colors"
          >
            <Phone size={16} aria-hidden="true" />
            Call {BUSINESS.phone}
          </a>
        </motion.div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section className="py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Core Services"
          title="Any mobile welding service you need"
          lead="Commercial, agricultural, and residential welding across Hardin, MT and beyond — big or small, we've got you covered."
        />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {SERVICE_ORDER.map((key, i) => {
            const s = SERVICES[key];
            return (
              <Reveal key={key} delay={i * 0.07} className="h-full">
                <Link
                  to={`/${key}-welding${key === "repairs" ? "s" : ""}`}
                  data-testid={`service-card-${key}`}
                  className="group flex flex-col h-full bg-coal border border-line hover:border-gold/60 transition-colors"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={IMG(s.card.image)}
                      alt={s.card.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="font-mono text-xs text-gold tracking-[0.25em]">
                      0{i + 1}
                    </span>
                    <h3 className="font-display text-xl font-bold uppercase text-slate-100 mt-2">
                      {s.card.name}
                    </h3>
                    <p className="text-sm text-slate-400 mt-2 leading-relaxed flex-1">
                      {s.card.desc}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-gold">
                      Learn More
                      <ArrowRight
                        size={15}
                        aria-hidden="true"
                        className="group-hover:translate-x-1 transition-transform"
                      />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WorkPreview() {
  return (
    <section className="bg-coal border-y border-line py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <SectionHead
            eyebrow="Portfolio"
            title="The work speaks for itself"
            lead="Actual projects completed by Park and Spark Mobile Welding, LLC."
          />
          <Reveal delay={0.1}>
            <Link
              to="/our-work"
              data-testid="work-view-all-button"
              className="inline-flex items-center gap-2 border border-line bg-ink px-6 py-3 text-sm font-bold uppercase tracking-wider text-slate-100 hover:border-gold hover:text-gold transition-colors"
            >
              View Portfolio
              <ArrowRight size={15} aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-4">
          {WORK_PREVIEW.map((w, i) => {
            const item = GALLERY.find((g) => g.src === w.src) || w;
            return (
              <Reveal key={w.src} delay={(i % 3) * 0.06} className="h-full">
                <Link
                  to="/our-work"
                  data-testid={`home-gallery-item-${i}`}
                  className="group relative block h-full overflow-hidden border border-line"
                >
                  <img
                    src={IMG(item.src)}
                    alt={item.alt}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover group-hover:scale-[1.04] transition-transform duration-500"
                  />
                  <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/25 transition-colors" aria-hidden="true" />
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Why() {
  return (
    <section className="py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="Why Park & Spark" title="No job too big or too small" />
        <div className="mt-12 grid gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w, i) => {
            const Icon = WHY_ICONS[w.icon];
            return (
              <Reveal key={w.title} delay={i * 0.07} className="h-full">
                <div className="h-full bg-ink p-7 hover:bg-coal transition-colors">
                  <Icon size={26} className="text-gold" aria-hidden="true" />
                  <h3 className="font-display text-lg font-bold uppercase text-slate-100 mt-4 leading-snug">
                    {w.title}
                  </h3>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{w.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function AboutIntro() {
  return (
    <section className="bg-coal border-y border-line py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal className="relative">
          <div className="relative overflow-hidden border border-line">
            <img
              src={IMG("img_2030.jpg")}
              alt="Park & Spark welder fabricating steel"
              loading="lazy"
              className="w-full aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] object-cover"
            />
          </div>
          <div className="absolute -bottom-5 -right-3 sm:-right-5 bg-gold px-5 py-4 hidden sm:block">
            <p className="font-display text-3xl font-bold text-ink leading-none">20+</p>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink/80 mt-1">
              Years of Experience
            </p>
          </div>
        </Reveal>
        <div>
          <SectionHead
            eyebrow="About Us"
            title="A family-owned welding shop that comes to you"
          />
          <Reveal delay={0.1}>
            <p className="text-slate-400 mt-5 leading-relaxed">
              Park and Spark Mobile Welding, LLC is based in Hardin, MT. For over 20 years we've
              brought expert fabrication and welding to job sites across the region — accommodating
              large and small jobs, and supporting our customers with emergency fabrication and
              welding repairs.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {CAPABILITIES.map((c) => (
                <span
                  key={c}
                  className="border border-line bg-ink px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-slate-300"
                >
                  {c}
                </span>
              ))}
            </div>
            <Link
              to="/about"
              data-testid="about-learn-more-button"
              className="mt-8 inline-flex items-center gap-2 border border-line bg-ink px-6 py-3 text-sm font-bold uppercase tracking-wider text-slate-100 hover:border-gold hover:text-gold transition-colors"
            >
              Learn More
              <ArrowRight size={15} aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ServiceArea() {
  return (
    <section className="py-20 sm:py-24 bg-grid">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12 items-center">
        <div className="lg:col-span-2">
          <SectionHead
            eyebrow="Service Area"
            title="Based in Hardin, MT — on the road across the region"
            lead="We offer free estimates to clients within 100 miles — and we take on out-of-state work. Not sure if you're in range? Give us a call."
          />
          <Reveal delay={0.1}>
            <a
              href={BUSINESS.phoneHref}
              data-testid="service-area-call-button"
              className="mt-8 inline-flex items-center gap-2 bg-gold px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-ink hover:bg-gold-light transition-colors"
            >
              <Phone size={16} aria-hidden="true" />
              Call {BUSINESS.phone}
            </a>
          </Reveal>
        </div>
        <div className="lg:col-span-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {SERVICE_AREAS.map((area, i) => (
              <Reveal key={area} delay={i * 0.03}>
                <div className="border border-line bg-coal px-4 py-3.5 text-sm text-slate-200 hover:border-gold/50 transition-colors">
                  <MapPin size={13} className="text-gold inline-block mr-2 -mt-0.5" aria-hidden="true" />
                  {area}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Services />
      <WorkPreview />
      <Why />
      <AboutIntro />
      <ServiceArea />
      <CTASection />
    </>
  );
}
